function parseRevisionNumber(value) {
  if (value === undefined || value === null || value === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseRevisionTimestamp(value) {
  if (value === undefined || value === null || value === "") {
    return null;
  }
  const parsed = Date.parse(value instanceof Date ? value.toISOString() : `${value}`);
  return Number.isNaN(parsed) ? null : parsed;
}

function compareOptionalNumbers(left, right) {
  if (left === right) {
    return 0;
  }
  if (left === null || left === undefined) {
    return -1;
  }
  if (right === null || right === undefined) {
    return 1;
  }
  return left > right ? 1 : -1;
}

export function getFlowSourceId(flow) {
  const source = flow?.replication?.source;
  return source ? `${source}` : null;
}

export function getPublishedFlowRevision(flow = {}) {
  const published = flow?.published && typeof flow.published === "object" ? flow.published : {};
  const hasPublishedState = flow?.published === true || Object.keys(published).length > 0;
  const version = parseRevisionNumber(published?.version);
  return {
    version: version ?? (hasPublishedState ? 1 : null),
    publishedAt:
      published?.published_date ??
      published?.publishedAt ??
      published?.at ??
      published?.updatedAt ??
      null,
  };
}

export function getReplicatedFlowRevision(flow = {}) {
  const replication = flow?.replication && typeof flow.replication === "object" ? flow.replication : {};
  const hasReplicationState = Boolean(replication?.source);
  const version = parseRevisionNumber(replication?.sourceVersion);
  return {
    version: version ?? (hasReplicationState ? 1 : null),
    publishedAt:
      replication?.sourcePublishedAt ??
      replication?.published_date ??
      replication?.sourcePublishedDate ??
      null,
  };
}

export function compareFlowRevisionInfo(left = {}, right = {}) {
  const leftVersion = parseRevisionNumber(left?.version);
  const rightVersion = parseRevisionNumber(right?.version);
  if (leftVersion !== null || rightVersion !== null) {
    return compareOptionalNumbers(leftVersion, rightVersion);
  }

  return compareOptionalNumbers(
    parseRevisionTimestamp(left?.publishedAt),
    parseRevisionTimestamp(right?.publishedAt)
  );
}

function compareReplicaPriority(left, right) {
  const revisionComparison = compareFlowRevisionInfo(
    getReplicatedFlowRevision(left),
    getReplicatedFlowRevision(right)
  );
  if (revisionComparison !== 0) {
    return revisionComparison;
  }

  const leftPlainId = Number(left?.plainId);
  const rightPlainId = Number(right?.plainId);
  return compareOptionalNumbers(
    Number.isFinite(leftPlainId) ? leftPlainId : null,
    Number.isFinite(rightPlainId) ? rightPlainId : null
  );
}

export function pickNewestReplica(flows = []) {
  return flows.reduce((best, candidate) => {
    if (!candidate) {
      return best;
    }
    if (!best) {
      return candidate;
    }
    return compareReplicaPriority(candidate, best) > 0 ? candidate : best;
  }, null);
}

export function buildLatestReplicaMap(flows = []) {
  const grouped = new Map();
  flows.forEach((flow) => {
    const sourceId = getFlowSourceId(flow);
    if (!sourceId) {
      return;
    }
    const current = grouped.get(sourceId) ?? null;
    if (!current || compareReplicaPriority(flow, current) > 0) {
      grouped.set(sourceId, flow);
    }
  });
  return grouped;
}

export function compareReplicaToPublished(replica, publishedFlow) {
  return compareFlowRevisionInfo(
    getReplicatedFlowRevision(replica),
    getPublishedFlowRevision(publishedFlow)
  );
}
